<script setup lang="ts">
/**
 * Wagtail user bar for headless frontend.
 * Fetches the userbar HTML from the CMS and loads Wagtail admin scripts so that
 * the bar works (e.g. edit link, accessibility checker, content metrics).
 * Pass `pageId` (same as pages API detail `id`) so edit / explorer / add-child target the current page.
 * See: https://docs.wagtail.org/en/latest/advanced_topics/headless.html#user-bar
 *
 * Inside Wagtail's preview iframe, set `inPreviewPanel` so the server returns the
 * accessibility-checker-only userbar (visible bar hidden via `<aside hidden>`) and the
 * admin "Checks" panel receives `w-userbar:axe-ready` postMessages from this iframe.
 */
import { ref, onMounted, onUnmounted, watch, nextTick } from 'vue';

const props = withDefaults(
  defineProps<{
    /** When true, the userbar container is hidden and no fetch/inject runs. */
    hidden?: boolean;
    /** Wagtail page id for the current route (pages API detail id). */
    pageId?: number | null;
    /**
     * True when the SPA is rendered inside Wagtail's preview iframe. Adds
     * `?in_preview_panel=1` so the backend renders the preview-panel-only userbar
     * (accessibility item + `<aside hidden>`) — axe still runs and posts results to the parent admin.
     */
    inPreviewPanel?: boolean;
    /**
     * When false, skip fetch/inject (and tear down any prior inject). Used in preview so we
     * only mount `<wagtail-userbar>` after StreamField HTML exists — Wagtail runs axe once on connect.
     */
    injectAllowed?: boolean;
  }>(),
  { hidden: false, pageId: null, inPreviewPanel: false, injectAllowed: true }
);

const userbarRef = ref<HTMLDivElement | null>(null);
const scriptElements: HTMLScriptElement[] = [];
/** Ignores stale async completion when `pageId` or visibility changes quickly. */
let injectGeneration = 0;
/**
 * Wagtail admin scripts stay in JS memory after their `<script>` nodes are removed.
 * `userbar.js` calls `customElements.define('wagtail-userbar', …)` once per execution;
 * reloading it on SPA navigation throws NotSupportedError, so we only bootstrap once.
 */
let wagtailUserbarVendorLoaded = false;
const WAGTAIL_USERBAR_TAG = 'wagtail-userbar';

const VENDOR_SCRIPT = '/static/wagtailadmin/js/vendor.js';
const USERBAR_SCRIPT = '/static/wagtailadmin/js/userbar.js';

function userbarFetchUrl(): string {
  const base = '/userbar/';
  const q = new URLSearchParams();
  if (props.pageId != null && !Number.isNaN(props.pageId)) {
    q.set('page_id', String(props.pageId));
  }
  if (props.inPreviewPanel) {
    q.set('in_preview_panel', '1');
  }
  const qs = q.toString();
  return qs ? `${base}?${qs}` : base;
}

function loadScript(src: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = src;
    script.async = false;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error(`Failed to load ${src}`));
    document.body.appendChild(script);
    scriptElements.push(script);
  });
}

function cleanupScripts() {
  for (const el of scriptElements) {
    el.remove();
  }
  scriptElements.length = 0;
}

async function injectUserbar() {
  const container = userbarRef.value;
  if (!container) return;
  if (!props.injectAllowed) return;

  const gen = ++injectGeneration;
  cleanupScripts();
  container.innerHTML = '';

  try {
    if (props.inPreviewPanel) {
      await nextTick();
      await new Promise<void>((resolve) => {
        requestAnimationFrame(() => resolve());
      });
    }
    if (gen !== injectGeneration || !props.injectAllowed) return;

    const res = await fetch(userbarFetchUrl(), { credentials: 'same-origin' });
    const html = await res.text();
    if (!res.ok || gen !== injectGeneration || !props.injectAllowed) return;

    container.innerHTML = html;

    // Order matters: vendor.js must run before userbar.js so axe-core globals
    // are available when `customElements.define('wagtail-userbar', …)` upgrades
    // the element and `initializeAxe()` runs against the shadow root.
    if (!customElements.get(WAGTAIL_USERBAR_TAG)) {
      if (!wagtailUserbarVendorLoaded) {
        await loadScript(VENDOR_SCRIPT);
        wagtailUserbarVendorLoaded = true;
        if (gen !== injectGeneration || !props.injectAllowed) return;
      }
      await loadScript(USERBAR_SCRIPT);
      if (gen !== injectGeneration || !props.injectAllowed) return;
    }
  } catch {
    // Silently ignore (e.g. CMS unreachable, not in preview)
  }
}

onMounted(() => {
  if (!props.hidden && props.injectAllowed) {
    void injectUserbar();
  }
});

onUnmounted(() => {
  injectGeneration += 1;
  cleanupScripts();
});

watch(
  () => [props.hidden, props.injectAllowed, props.pageId, props.inPreviewPanel] as const,
  ([hidden, injectAllowed]) => {
    if (hidden || !injectAllowed) {
      injectGeneration += 1;
      cleanupScripts();
      if (userbarRef.value) userbarRef.value.innerHTML = '';
    } else {
      void injectUserbar();
    }
  }
);
</script>

<template>
  <div
    ref="userbarRef"
    :hidden="hidden"
    :aria-hidden="hidden"
    class="wagtail-userbar-container"
  />
</template>

<style scoped>
/*
 * Mount point only — must NOT establish a positioning context.
 * The shadow-DOM accessibility-checker outline is `position: absolute` with
 * coordinates in document space (`top: rect.top + scrollY`). Any positioned
 * ancestor (fixed/relative/absolute) on this wrapper would become the outline's
 * containing block and break "Show issue" highlighting on scrolled pages.
 * Wagtail's own `.w-userbar` inside the shadow root already handles
 * `position: fixed; z-index: 9999;` for the visible bar.
 */
.wagtail-userbar-container {
  display: contents;
}
.wagtail-userbar-container[hidden] {
  display: none;
}
</style>
